import { convertToMp3, isMp3Name } from '@/lib/audioMp3'
import { describeError, logError } from '@/lib/errors'
import { lumi } from '@/lib/lumi'

// Limite do armazenamento da plataforma por ficheiro.
const MAX_UPLOAD_SIZE = 10 * 1024 * 1024
// Acima deste tamanho, a música é recomprimida em MP3 antes de enviar.
const COMPRESS_ABOVE = 9 * 1024 * 1024
// Limite do ficheiro original aceite para tentar comprimir.
const MAX_SOURCE_SIZE = 60 * 1024 * 1024
const AUDIO_EXTENSIONS = ['mp3', 'mpeg', 'mpg', 'mpga', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'flac', 'opus', 'aiff', 'wma']

type UploadResult = {
  fileName?: string
  fileUrl?: string
  uploadError?: string
}

export interface UploadedAudio {
  url: string
  fileName: string
  sizeBytes: number
  converted: boolean
}

export function isAudioFile(file: File) {
  const ext = (file.name.split('.').pop() || '').toLowerCase()
  return file.type.startsWith('audio/') || AUDIO_EXTENSIONS.includes(ext)
}

export function titleFromFileName(name: string) {
  return name
    .replace(/\.[^.]+$/, '')
    .replace(/[_]+/g, ' ')
    .trim()
}

function toMp3File(blob: Blob, originalName: string) {
  const base = originalName.replace(/\.[^.]+$/, '')
  return new File([blob], `${base}.mp3`, { type: 'audio/mpeg' })
}

async function sendFile(file: File): Promise<string> {
  let raw: unknown
  try {
    raw = await lumi.tools.file.upload([file])
  } catch (error) {
    logError('upload de áudio', error)
    const reason = describeError(error)
    throw new Error(
      reason === 'Ocorreu um erro inesperado.'
        ? `O servidor recusou "${file.name}". Tente um arquivo menor (até 10 MB).`
        : `Falha ao enviar "${file.name}": ${reason}`,
    )
  }
  const list = (Array.isArray(raw) ? raw : [raw]) as UploadResult[]
  const result = list[0]
  if (!result?.fileUrl) {
    throw new Error(result?.uploadError ? `"${file.name}": ${result.uploadError}` : `Falha ao enviar "${file.name}".`)
  }
  return result.fileUrl
}

/** Valida, converte para MP3 (quando preciso), comprime se for grande e envia o áudio. */
export async function uploadAudio(file: File): Promise<UploadedAudio> {
  if (!isAudioFile(file)) {
    throw new Error(`"${file.name}" não é um áudio reconhecido. Use MP3, MPEG, WAV, OGG, M4A, AAC ou FLAC.`)
  }
  if (file.size > MAX_SOURCE_SIZE) {
    throw new Error(`"${file.name}" é grande demais (máximo 60 MB).`)
  }

  let toSend: File = file
  let converted = false
  const needsConversion = !isMp3Name(file.name)
  const needsCompression = file.size > COMPRESS_ABOVE

  if (needsConversion || needsCompression) {
    try {
      toSend = toMp3File(await convertToMp3(file), file.name)
      converted = true
    } catch (error) {
      logError('conversão para MP3', error)
      if (needsCompression) {
        throw new Error(`Não foi possível reduzir "${file.name}" para menos de 10 MB. Envie um MP3 menor.`)
      }
    }
  }

  if (toSend.size > MAX_UPLOAD_SIZE) {
    throw new Error(`"${file.name}" continua com mais de 10 MB mesmo comprimido. Envie uma versão mais curta.`)
  }

  const url = await sendFile(toSend)
  return { url, fileName: toSend.name, sizeBytes: toSend.size, converted }
}
